/**
 * `plain_text` and `headings` — the two derived columns the search index reads
 * (§9.1).
 *
 * §9.1: "generate `plain_text` at write time (strip markdown syntax — `##` and
 * raw URLs should not pollute matches)." So this walks the JSON rather than
 * running a regex over the derived markdown: a regex would have to know every
 * construct the serializer can emit, which is a third place the component set
 * is written down and the first one to go stale.
 *
 * What is deliberately *not* in the output:
 *
 * - **URLs.** A link contributes its text, not its href, and an embed
 *   contributes its title if it has one. §9.1 names raw URLs specifically:
 *   searching for "wordpress" should not match every document linking to
 *   wordpress.org.
 * - **Block ids, fence info, directive attributes.** Machine tokens.
 * - **The headings.** They are indexed separately at weight `B`
 *   (`document.headings`), and including them here would index them twice at
 *   two weights, which quietly flattens the ranking §9.1 asks for.
 *
 * Code blocks *are* included. "Where is the nginx config for staging" is a real
 * search, and the answer is inside a fenced block.
 */

import { normalizeDocument } from './schema.ts';
import type { DocumentJSON, NodeJSON } from './types.ts';

/** Everything a person could search for, one block per line. */
export function documentToPlainText(doc: DocumentJSON): string {
  const lines: string[] = [];
  collect(normalizeDocument(doc), lines, false);
  return lines.join('\n');
}

/** The heading text, one per line — `document.headings`, weight `B` in §9.1. */
export function documentHeadings(doc: DocumentJSON): string {
  const lines: string[] = [];
  collect(normalizeDocument(doc), lines, true);
  return lines.join('\n');
}

function collect(node: NodeJSON, lines: string[], headingsOnly: boolean): void {
  switch (node.type) {
    case 'heading': {
      const text = inlineText(node.content ?? []);
      if (text) lines.push(text);
      return;
    }
    case 'paragraph':
    case 'codeBlock': {
      if (headingsOnly) return;
      const text = inlineText(node.content ?? []);
      if (text) lines.push(text);
      return;
    }
    case 'tableRow': {
      if (headingsOnly) return;
      // A row on one line: "Environment Staging" is a sentence a person might
      // search for; the same words split across three lines are not.
      const cells = (node.content ?? []).map((cell) => inlineText(cell.content ?? []));
      const text = cells.filter(Boolean).join(' ');
      if (text) lines.push(text);
      return;
    }
    case 'image': {
      if (headingsOnly) return;
      const alt = node.attrs?.alt;
      if (typeof alt === 'string' && alt) lines.push(alt);
      return;
    }
    case 'embed': {
      if (headingsOnly) return;
      const title = node.attrs?.title;
      if (typeof title === 'string' && title) lines.push(title);
      return;
    }
    case 'horizontalRule':
    case 'liveData':
      // Nothing a person searches for. A live-data embed's text does not exist
      // until it resolves, and §13 defers that past Phase 1 entirely.
      return;
    default:
      for (const child of node.content ?? []) collect(child, lines, headingsOnly);
  }
}

function inlineText(nodes: NodeJSON[]): string {
  let out = '';
  for (const node of nodes) {
    if (node.type === 'hardBreak') out += ' ';
    else if (node.text) out += node.text;
  }
  return out.trim();
}
